class FloatingAddToCart extends HTMLElement {
  connectedCallback() {
    this.floatingButton = this.querySelector('button[type="submit"]');
    if (!this.floatingButton) return;

    this.animQueue = null;
    this.mainButton = this.findMainButton();
    this.productForm = this.findProductForm();

    // Handle button click to submit form with proper button reference
    this.floatingButton.addEventListener('click', this.handleSubmit.bind(this));

    // Sync loading state when main button changes
    if (this.mainButton) {
      this.observeMainButton();
    }

    // Initialize scroll observer for mobile floating button behavior
    this.initScrollObserver();
  }

  disconnectedCallback() {
    this.floatingButton?.removeEventListener('click', this.handleSubmit.bind(this));
    this.observer?.disconnect();
    this.scrollObserver?.disconnect();
    clearTimeout(this.animQueue);
  }

  /**
   * Handle form submission with proper button reference
   */
  handleSubmit(evt) {
    evt.preventDefault();

    if (!this.productForm || typeof this.productForm.onSubmitHandler !== 'function') {
      // Fallback to native form submission if product-form handler not available
      const formId = this.floatingButton.getAttribute('form');
      if (formId) {
        const form = document.getElementById(formId);
        if (form) {
          form.requestSubmit();
        }
      }
      return;
    }

    // Call product-form's onSubmitHandler with floating button as submitButtonEl
    this.productForm.onSubmitHandler(evt, this.floatingButton);
  }

  /**
   * Find the product-form component
   * @returns {HTMLElement|null} The product-form element or null if not found
   */
  findProductForm() {
    const formId = this.floatingButton?.getAttribute('form');
    if (formId) {
      const form = document.getElementById(formId);
      if (form) {
        return form.closest('product-form');
      }
    }
    return document.querySelector('[data-main-product-form]');
  }

  /**
   * Find the main add to cart button to sync loading state
   * @returns {HTMLElement|null} The main button element or null if not found
   */
  findMainButton() {
    // Extract section ID from component's ID attribute (format: floating-add-to-cart-${sectionId})
    const sectionId = this.id?.match(/^floating-add-to-cart-(.+)$/)?.[1];

    if (sectionId) {
      return document.querySelector(`#AddToCart-${sectionId}`);
    }

    return null;
  }

  /**
   * Observe the main button to sync loading state
   */
  observeMainButton() {
    this.observer = new MutationObserver(() => {
      this.syncLoadingState();
    });

    this.observer.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class'],
    });

    // Also listen to class changes on the button's parent
    const mainButtonParent = this.mainButton.closest('[data-floating-add-to-cart]');
    if (mainButtonParent) {
      this.observer.observe(mainButtonParent, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Initial sync
    this.syncLoadingState();
  }

  /**
   * Sync loading state between floating button and main button
   */
  syncLoadingState() {
    const isDisabled = this.mainButton.hasAttribute('disabled');
    const isLoading = this.mainButton.classList.contains('loading');

    if (isDisabled || isLoading) {
      this.floatingButton.setAttribute('disabled', '');
      this.floatingButton.classList.add('loading');
    } else {
      this.floatingButton.removeAttribute('disabled');
      this.floatingButton.classList.remove('loading');
    }
  }

  /**
   * Initialize IntersectionObserver for scroll-based desktop floating button visibility
   * Shows/hides desktop floating button based on buy button visibility
   */
  initScrollObserver() {
    const regularBuyButton = document.querySelector('[data-observer-buy-buttons]');

    if (!regularBuyButton) {
      return;
    }

    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Only handle desktop logic
        if (window.innerWidth <= 1024) return;

        const desktopFloatingButton = document.querySelector('floating-add-to-cart');
        if (!desktopFloatingButton) return;

        if (entry.isIntersecting) {
          // Buy button is visible - hide desktop floating button
          desktopFloatingButton.classList.remove('active');
        } else {
          // Buy button is not visible - show desktop floating button
          desktopFloatingButton.classList.add('active');
        }
      });
    });

    this.scrollObserver.observe(regularBuyButton);
  }
}

customElements.define('floating-add-to-cart', FloatingAddToCart);
